import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import HealthCalculators from "@/components/HealthCalculators";
import PwaInstall from "@/components/PwaInstall";
import { CalendarHeart, CaretRight, UserCircle, User, UserPlus, EnvelopeSimple, LockSimple, SignIn, Eye, EyeSlash, HeartStraight, BookOpen, MusicNotes, Storefront, ChatsCircle } from "@phosphor-icons/react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) navigate(location.state?.from || "/", { replace: true });
    else setError(res.error);
  };

  const inputCls =
    "w-full rounded-xl border border-pink-100 bg-[#FDFBFE] py-3 pl-11 pr-4 text-sm text-slate-800 outline-none transition-colors placeholder:text-slate-400 focus:border-[#EC4899] focus:bg-white focus:ring-2 focus:ring-[#EC4899]/15";

  return (
    <div className="relative flex min-h-screen w-full flex-col items-center justify-center overflow-hidden bg-gradient-to-b from-[#FCE7F3] via-[#FBEFF6] to-[#FDF2F8] px-4 py-10">
      {/* Soft decorative hearts */}
      <HeartStraight size={90} weight="fill" className="pointer-events-none absolute -left-6 top-24 text-pink-200/50" />
      <HeartStraight size={64} weight="fill" className="pointer-events-none absolute right-2 top-40 text-pink-200/40" />
      <HeartStraight size={110} weight="fill" className="pointer-events-none absolute -right-8 bottom-20 text-pink-200/40" />
      <HeartStraight size={48} weight="fill" className="pointer-events-none absolute left-4 bottom-28 text-pink-200/50" />

      <div className="animate-fade-up relative z-10 w-full max-w-md space-y-6">
        {/* Logo + welcome */}
        <div className="flex flex-col items-center text-center">
          <img src={`${process.env.PUBLIC_URL}/logo.webp`} alt="GBS Clinic & Birth Center" className="mb-3 h-24 w-auto object-contain" />
          <h1 className="font-heading text-[26px] font-extrabold leading-snug tracking-tight text-[#1E1B4B] sm:text-3xl">
            Selamat Datang di
            <br />
            <span className="text-[#1E1B4B]">GBS </span>
            <span className="bg-gradient-to-r from-[#EC4899] to-[#DB2777] bg-clip-text text-transparent">Clinic &amp; Birth Center</span>
          </h1>
          <p className="mt-2 text-sm text-slate-500 sm:text-base">Reservasi layanan GBS dengan mudah dan cepat.</p>
        </div>

        {/* PRIMARY CTA — pink gradient card */}
        <Link
          data-testid="public-booking-link"
          to="/booking"
          className="group flex items-center gap-4 rounded-3xl bg-gradient-to-br from-[#F472B6] via-[#EC4899] to-[#DB2777] p-5 shadow-[0_16px_34px_-12px_rgba(236,72,153,0.6)] transition-transform duration-200 hover:-translate-y-0.5 active:scale-[0.99]"
        >
          <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-white/90 shadow-inner">
            <CalendarHeart size={30} weight="duotone" className="text-[#EC4899]" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block font-heading text-xl font-extrabold leading-tight text-white">Buat Reservasi Tanpa Login</span>
            <span className="mt-1 block text-xs leading-snug text-white/90">Untuk pasien yang ingin langsung membuat reservasi tanpa akun.</span>
          </span>
          <CaretRight size={22} weight="bold" className="shrink-0 text-white transition-transform duration-200 group-hover:translate-x-1" />
        </Link>

        <Link
          data-testid="public-consult-online-card"
          to="/konsultasi-sekarang"
          className="group flex items-center gap-4 rounded-3xl bg-gradient-to-br from-[#EC4899] via-[#C026D3] to-[#7C3AED] p-5 shadow-[0_16px_34px_-12px_rgba(124,58,237,0.55)] transition-transform duration-200 hover:-translate-y-0.5 active:scale-[0.99]"
        >
          <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-white/90 shadow-inner">
            <ChatsCircle size={30} weight="duotone" className="text-[#9333EA]" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block font-heading text-xl font-extrabold leading-tight text-white">Chat Dokter &amp; Bidan Online</span>
            <span className="mt-1 block text-xs leading-snug text-white/90">Konsultasi melalui chat dengan tenaga kesehatan yang tersedia.</span>
            <span data-testid="public-consult-online-btn" className="mt-3 inline-flex items-center gap-1 rounded-full bg-white px-4 py-1.5 text-xs font-bold text-[#7C3AED] shadow-sm">Lihat Ketersediaan <CaretRight size={12} weight="bold" className="transition-transform duration-200 group-hover:translate-x-0.5" /></span>
          </span>
        </Link>

        {/* Secondary CTA — Artikel & Edukasi (subordinate to reservation CTA) */}
        <Link
          data-testid="public-articles-card"
          to="/artikel"
          className="group flex items-center gap-3 rounded-2xl border border-[#E5D9FF] bg-white/80 px-4 py-3 transition-transform duration-200 hover:-translate-y-0.5 active:scale-[0.99]"
        >
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#EEE6FF]">
            <BookOpen size={20} weight="duotone" className="text-[#7C4DE0]" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block font-heading text-sm font-extrabold leading-tight text-[#4C1D95]">Artikel &amp; Edukasi GBS</span>
            <span className="mt-0.5 block text-xs leading-snug text-slate-500">Informasi seputar kehamilan, persalinan, ibu, bayi, dan keluarga.</span>
          </span>
          <CaretRight size={18} weight="bold" className="shrink-0 text-[#7C4DE0] transition-transform duration-200 group-hover:translate-x-1" />
        </Link>

        {/* Relaksasi catalog entry */}
        <a
          href="/relaksasi"
          data-testid="relax-landing-card"
          className="group flex w-full items-center gap-3 rounded-2xl border border-[#F5C6E0] bg-white/80 px-4 py-3 text-left transition-transform duration-200 hover:-translate-y-0.5 active:scale-[0.99]"
        >
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#EEE6FF] to-[#FCE7F3]">
            <MusicNotes size={20} weight="duotone" className="text-[#DB2777]" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block font-heading text-sm font-extrabold leading-tight text-[#4C1D95]">Relaksasi</span>
            <span className="mt-0.5 block text-xs leading-snug text-slate-500">Audio hypnobirthing & hypno-breastfeeding — dengar cuplikan gratis.</span>
          </span>
          <CaretRight size={18} weight="bold" className="shrink-0 text-[#DB2777] transition-transform duration-200 group-hover:translate-x-1" />
        </a>

        {/* Belanja Produk — public store entry */}
        <Link
          to="/belanja"
          data-testid="shop-landing-card"
          className="group flex w-full items-center gap-3 rounded-2xl border border-[#DDD0FF] bg-white/80 px-4 py-3 text-left transition-transform duration-200 hover:-translate-y-0.5 active:scale-[0.99]"
        >
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#FCE7F3] to-[#EEE6FF]">
            <Storefront size={20} weight="duotone" className="text-[#7C4DE0]" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block font-heading text-sm font-extrabold leading-tight text-[#4C1D95]">Belanja Produk GBS</span>
            <span className="mt-0.5 block text-xs leading-snug text-slate-500">Produk pilihan untuk ibu & bayi — lihat katalog tanpa perlu login.</span>
          </span>
          <CaretRight size={18} weight="bold" className="shrink-0 text-[#7C4DE0] transition-transform duration-200 group-hover:translate-x-1" />
        </Link>

        {/* Secondary CTA — Install PWA (subordinate to reservation CTA) */}
        <PwaInstall />

        {/* Health calculators (public education tools) */}
        <HealthCalculators />

        {/* Patient account card — right after the calculators (Timer Kontraksi), before the "ATAU" separator */}
        <section data-testid="patient-account-card" className="rounded-3xl border border-[#DDD0FF] bg-white/90 p-5 shadow-[0_10px_30px_-16px_rgba(124,77,224,0.3)]">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#EEE6FF]">
              <User size={20} weight="duotone" className="text-[#7C4DE0]" />
            </span>
            <div className="min-w-0">
              <h2 className="font-heading text-base font-extrabold leading-tight text-[#4C1D95]">Akun Pasien HalloGBS</h2>
              <p className="mt-0.5 text-xs leading-snug text-slate-500">Masuk atau daftar untuk membeli dan mendengarkan audio relaksasi.</p>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-3">
            <Link data-testid="patient-login-btn" to="/pasien/masuk" className="flex items-center justify-center gap-1.5 rounded-xl bg-[#7C4DE0] py-3 text-sm font-bold text-white shadow-sm transition-colors hover:bg-[#6A38C7] active:scale-95">
              <SignIn size={17} weight="bold" /> Masuk
            </Link>
            <Link data-testid="patient-register-btn" to="/pasien/daftar" className="flex items-center justify-center gap-1.5 rounded-xl border border-[#7C4DE0] bg-white py-3 text-sm font-bold text-[#7C4DE0] transition-colors hover:bg-[#F5F0FF] active:scale-95">
              <UserPlus size={17} weight="bold" /> Daftar Akun
            </Link>
          </div>
        </section>


        {/* Separator */}
        <div className="flex items-center gap-3">
          <HeartStraight size={16} weight="fill" className="text-pink-300" />
          <span className="h-px flex-1 bg-pink-200" />
          <span className="rounded-full bg-white/70 px-3 py-1 text-xs font-bold uppercase tracking-widest text-[#DB2777]">atau</span>
          <span className="h-px flex-1 bg-pink-200" />
          <HeartStraight size={16} weight="fill" className="text-pink-300" />
        </div>

        {/* Admin/Petugas login (secondary) */}
        <div className="relative rounded-3xl border border-pink-100 bg-white p-6 pt-9 shadow-[0_10px_30px_-14px_rgba(124,77,224,0.25)] sm:p-7 sm:pt-9">
          <span className="absolute -top-6 left-1/2 flex h-12 w-12 -translate-x-1/2 items-center justify-center rounded-full bg-[#7C4DE0] shadow-lg">
            <UserCircle size={26} weight="fill" className="text-white" />
          </span>
          <h2 className="mb-4 text-center font-heading text-lg font-bold text-[#1E1B4B]">Login Admin / Petugas</h2>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="mb-1.5 block text-xs font-bold uppercase tracking-widest text-slate-500">Email</label>
              <div className="relative">
                <EnvelopeSimple size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-pink-400" />
                <input data-testid="login-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="email@contoh.com" className={inputCls} />
              </div>
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-bold uppercase tracking-widest text-slate-500">Password</label>
              <div className="relative">
                <LockSimple size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-pink-400" />
                <input data-testid="login-password-input" type={show ? "text" : "password"} required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className={inputCls + " pr-11"} />
                <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-[#7C4DE0]" aria-label="Tampilkan / sembunyikan password">
                  {show ? <EyeSlash size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            {error && <p data-testid="login-error" className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-600">{error}</p>}

            <button data-testid="login-submit-button" type="submit" disabled={loading} className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#7C4DE0] py-3 text-sm font-bold text-white shadow-sm transition-colors hover:bg-[#6A38C7] active:scale-95 disabled:opacity-60">
              <SignIn size={18} weight="bold" /> {loading ? "Memproses..." : "Masuk"}
            </button>

            <div className="pt-1 text-center">
              <Link data-testid="forgot-password-link" to="/forgot-password" className="text-sm font-semibold text-[#7C4DE0] hover:underline">Lupa Password / Aktivasi Akun</Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
